import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { useGlobalState } from '../GlobalState';
import PaymentOptionMarks from './PaymentOptionMarks';
import PaymentCountdownTimer from './PaymentCountdownTimer';
import PaymentMethodDrawer from './PaymentMethodDrawer';
import useApplePay from './useApplePay';
import '../../assets/css/payment_page.css';
import '../../assets/css/payment_form.css';
import { apiSlice } from '../../../../store/apiSlice';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useReevaluateEngagementMutation } from '../../../user_profile/userProfileSlice';
import StripePaymentWrapper from './StripePaymentWrapper';
import StripePaymentForm from './StripePaymentForm';
import RyftPaymentForm from './RyftPaymentForm';

export const PaymentProvider = {
  OPAYO: 'opayo',
  STRIPE: 'stripe',
  RYFT: 'ryft',
};

function PaymentPage({
  idField,
  amount,
  billingDetails = {},
  paymentProvider = PaymentProvider.OPAYO,
  enableStoredCards = false,
  enableApplePay = false,
  enableSaveCard = false,
  onPaymentSuccess = () => {},
  setErrorPayment = () => {},
  onPaymentFailure = () => {},
  title = 'Payment',
  headerContent = null,
  paymentExpiryTime = null,
  onClickCountdownEndedBack = () => {},
  countdownEndedExpiryMessage = 'Your reserved slots have expired, please try again.',
  countdownEndedBackMessage = 'Back to booking page',
}) {
  const dispatch = useDispatch();
  const [storedCards] = useGlobalState('storedCards');
  const [storedCardsError] = useGlobalState('storedCardsError');
  const [paymentFormData] = useGlobalState('paymentFormData');
  const [triggerEngagementReevaluation] = useReevaluateEngagementMutation();

  const anyStoredCards = Boolean(
    (storedCards?.length || storedCardsError) && !paymentFormData
  );
  const [showStoredCardsFooter, setShowStoredCardsFooter] = useState(
    enableStoredCards && anyStoredCards
  );

  const onPaymentSuccessInternal = () => {
    dispatch(apiSlice.util.invalidateTags(['CreditHistory']));
    axios.post('webhooks/trigger_pending');
    triggerEngagementReevaluation();
    onPaymentSuccess();
  };

  const [hasApplePay, applePayButton] = useApplePay({
    onSuccess: onPaymentSuccessInternal,
    onFailure: onPaymentFailure,
    disabled: !enableApplePay || paymentProvider !== PaymentProvider.OPAYO,
  });

  const showApplePay = hasApplePay && enableApplePay;

  useEffect(() => {
    window.scrollTo(0, 0);
    window.onbeforeunload = () => 'true';
    return () => (window.onbeforeunload = null);
  }, []);

  useEffect(() => {
    if (anyStoredCards) {
      setShowStoredCardsFooter(enableStoredCards && anyStoredCards);
    }
  }, [anyStoredCards, enableStoredCards]);

  const header = (
    <div className={'txt-center gRegular fs-16 mg-right-15 mg-left-15'}>
      <div className={'mg-bottom-10'}>
        <PaymentOptionMarks showApplePay={showApplePay} />
      </div>
      <h2 className={'gSemiBold fs-28'}>{title}</h2>
      {headerContent}
      {paymentExpiryTime && (
        <PaymentCountdownTimer
          paymentExpiryTime={paymentExpiryTime}
          onClickCountdownEndedBack={onClickCountdownEndedBack}
          expiryMessage={countdownEndedExpiryMessage}
          backMessage={countdownEndedBackMessage}
        />
      )}
    </div>
  );

  const changePaymentMethodButton = (
    <Button
      onClick={() => setShowStoredCardsFooter(true)}
      className='payment_form__button--inset txt-center br5 gSemiBold fs-18 w-full pd-7 pd-left-15 pd-right-15 h-full brFull mg-auto-lr text-wrap'
    >
      Change Payment Method
    </Button>
  );

  return (
    <div
      className={
        'pd-top-30 pd-left-25 pd-right-25 pd-bottom-20 display-flex-column justify-center'
      }
    >
      {header}
      {showApplePay && Boolean(applePayButton) && (
        <div className={'pd-bottom-15'}>{applePayButton}</div>
      )}
      {Boolean(storedCards?.length) &&
        enableStoredCards &&
        paymentProvider === PaymentProvider.Opayo &&
        changePaymentMethodButton}
      {paymentProvider === PaymentProvider.STRIPE ? (
        <StripePaymentWrapper amount={amount} currency={'gbp'}>
          <StripePaymentForm
            idField={idField}
            amount={amount}
            billingDetails={billingDetails}
            onPaymentSuccess={onPaymentSuccessInternal}
            onPaymentFailure={onPaymentFailure}
          />
        </StripePaymentWrapper>
      ) : paymentProvider === PaymentProvider.RYFT ? (
        <RyftPaymentForm
          idField={idField}
          amount={amount}
          onPaymentSuccess={onPaymentSuccessInternal}
          onPaymentFailure={onPaymentFailure}
          enableSaveCard={enableSaveCard}
          enableWalletPayments={enableApplePay}
          enableStoredCardsUse={enableStoredCards}
        />
      ) : (
        <Alert
          className='gRegular br10 mg-bottom-20'
          message={<span className='gSemiBold'>Something went wrong!</span>}
          description='Unable to locate a valid payment provider from the current flag configurations.'
          type='error'
          showIcon
        />
      )}
      {enableStoredCards && paymentProvider === PaymentProvider.Opayo && (
        <PaymentMethodDrawer
          open={showStoredCardsFooter}
          setOpen={setShowStoredCardsFooter}
          setErrorPayment={setErrorPayment}
          showApplePay={showApplePay}
          amount={amount}
          onPaymentSuccess={onPaymentSuccessInternal}
          onPaymentFailure={onPaymentFailure}
        />
      )}
    </div>
  );
}

export default PaymentPage;
